import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/loading-state'
import { listEventRules, deleteEventRule } from '../api/derivationConfig'
import { EventRuleModal } from './EventRuleModal'
import type { EventRule } from '../types'

interface EventRulesTabProps {
  accId?: number
}

function ruleKey(r: EventRule) {
  return `${r.accId}-${r.eventType}-${r.category}-${r.eventName}`
}

export function EventRulesTab({ accId }: EventRulesTabProps) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<EventRule | undefined>()
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<EventRule | null>(null)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: rules = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['event-rules', accId],
    queryFn: () => listEventRules(accId),
  })

  const deleteMutation = useMutation({
    mutationFn: (r: EventRule) => deleteEventRule(r.accId, r.eventType, r.category, r.eventName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-rules'] })
      setDeleteConfirm(null)
      showFeedback('success', 'Event rule deleted.')
    },
    onError: (err: Error) => {
      showFeedback('error', err.message)
      setDeleteConfirm(null)
    },
  })

  function showFeedback(type: 'success' | 'error', message: string) {
    setFeedback({ type, message })
    setTimeout(() => setFeedback(null), 3000)
  }

  function handleEdit(r: EventRule) {
    setEditTarget(r)
    setShowModal(true)
  }

  function handleCloseModal() {
    setShowModal(false)
    setEditTarget(undefined)
  }

  function toggleExpand(key: string) {
    setExpandedKey(prev => (prev === key ? null : key))
  }

  const UPDATE_MODE_COLORS: Record<string, string> = {
    SET: 'bg-blue-50 text-blue-700',
    SET_IF_NULL: 'bg-purple-50 text-purple-700',
    INC: 'bg-green-50 text-green-700',
    INCREMENT: 'bg-green-50 text-green-700',
    MAX: 'bg-orange-50 text-orange-700',
    MAX_VALUE: 'bg-orange-50 text-orange-700',
    MIN: 'bg-yellow-50 text-yellow-700',
    MIN_VALUE: 'bg-yellow-50 text-yellow-700',
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Event Rules</h2>
          <p className="text-sm text-gray-500">
            One rule per event — defines which attributes map to which entity columns.
          </p>
        </div>
        <Button size="sm" onClick={() => { setEditTarget(undefined); setShowModal(true) }}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Event Rule
        </Button>
      </div>

      {feedback && (
        <div className={`mb-4 text-sm px-4 py-2 rounded-lg ${
          feedback.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        {isLoading ? (
          <LoadingState message="Loading event rules…" />
        ) : isError ? (
          <ErrorState error={error as Error} title="Failed to load event rules" onRetry={() => refetch()} />
        ) : rules.length === 0 ? (
          <EmptyState
            title="No event rules"
            description="Create your first event rule to start deriving entity updates from events."
            action={{ label: 'New Event Rule', onClick: () => setShowModal(true) }}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="w-8 px-2 py-3" />
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Acc ID</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Event Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Event Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Entity Key</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Derivations</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r, idx) => {
                  const key = ruleKey(r)
                  const isExpanded = expandedKey === key
                  const derivationEntries = Object.entries(r.derivations ?? {})

                  return (
                    <Fragment key={key}>
                      <tr
                        className={`border-b hover:bg-gray-50 transition-colors cursor-pointer ${
                          isExpanded ? 'bg-blue-50/30' : idx % 2 !== 0 ? 'bg-gray-50/40' : ''
                        }`}
                        onClick={() => toggleExpand(key)}
                      >
                        {/* Expand chevron */}
                        <td className="px-2 py-3 text-gray-400">
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-700">
                          {r.accId ?? <span className="text-gray-400 italic">global</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{r.eventType}</td>
                        <td className="px-4 py-3 text-gray-700">{r.category}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{r.eventName}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{r.entityKey}</code>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5">
                            <Badge variant="info">
                              {derivationEntries.length} attr{derivationEntries.length !== 1 ? 's' : ''}
                            </Badge>
                            {(r.arrayImpacts ?? []).length > 0 && (
                              <Badge variant="secondary">
                                {r.arrayImpacts!.length} array group{r.arrayImpacts!.length !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          {deleteConfirm === r ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-xs text-gray-500">Confirm delete?</span>
                              <Button size="sm" variant="destructive"
                                onClick={() => deleteMutation.mutate(r)}
                                disabled={deleteMutation.isPending}>
                                Yes
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)}>
                                No
                              </Button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(r)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost"
                                onClick={() => setDeleteConfirm(r)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded derivation detail */}
                      {isExpanded && (
                        <tr className="border-b bg-blue-50/20">
                          <td colSpan={8} className="px-6 py-4 space-y-5">

                            {/* Regular derivations */}
                            <div>
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Derivations
                              </div>
                              {derivationEntries.length === 0 ? (
                                <p className="text-sm text-gray-400 italic">No derivations defined.</p>
                              ) : (
                                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                                  <thead>
                                    <tr className="bg-gray-100 border-b border-gray-200">
                                      <th className="text-left px-3 py-2 font-medium text-gray-600 w-40">Attribute</th>
                                      <th className="text-left px-3 py-2 font-medium text-gray-600">Impacts (template refs)</th>
                                      <th className="text-left px-3 py-2 font-medium text-gray-600 w-28">Mode</th>
                                      <th className="text-left px-3 py-2 font-medium text-gray-600">Expression</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {derivationEntries.map(([attr, impacts]) =>
                                      (impacts ?? []).map((imp, impIdx) => (
                                        <tr
                                          key={`${attr}-${impIdx}`}
                                          className="border-b border-gray-100 last:border-0 hover:bg-white/60"
                                        >
                                          {impIdx === 0 ? (
                                            <td
                                              className="px-3 py-2 font-mono text-gray-800 align-top"
                                              rowSpan={(impacts ?? []).length}
                                            >
                                              {attr}
                                            </td>
                                          ) : null}
                                          <td className="px-3 py-2">
                                            <code className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                                              {imp.ref}
                                            </code>
                                          </td>
                                          <td className="px-3 py-2">
                                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                              UPDATE_MODE_COLORS[imp.mode ?? 'SET'] ?? 'bg-gray-100 text-gray-700'
                                            }`}>
                                              {imp.mode ?? 'SET'}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2">
                                            {imp.expr ? (
                                              <code className="text-xs bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">
                                                {imp.expr}
                                              </code>
                                            ) : (
                                              <span className="text-gray-300">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>

                            {/* Array impact groups */}
                            {(r.arrayImpacts ?? []).length > 0 && (
                              <div>
                                <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-3">
                                  Array Impacts
                                </div>
                                <div className="space-y-3">
                                  {(r.arrayImpacts ?? []).map((group, gi) => (
                                    <div key={gi} className="border border-indigo-200 rounded-lg overflow-hidden">
                                      <div className="bg-indigo-50 px-3 py-2 flex items-center gap-4 text-xs">
                                        <span className="font-semibold text-indigo-700">Group {gi + 1}</span>
                                        <span>
                                          Array Attr: <code className="font-mono bg-white px-1 py-0.5 rounded text-indigo-800">{group.arrayAttr}</code>
                                        </span>
                                        <span>
                                          Element Key: <code className="font-mono bg-white px-1 py-0.5 rounded text-indigo-800">{group.entityKey}</code>
                                        </span>
                                      </div>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="text-left px-3 py-2 font-medium text-gray-600 w-40">Attribute</th>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600">Template Ref</th>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600 w-28">Mode</th>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600">Expression</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Object.entries(group.derivations ?? {}).map(([attr, impacts]) =>
                                            (impacts ?? []).map((imp, impIdx) => (
                                              <tr
                                                key={`${attr}-${impIdx}`}
                                                className="border-b border-gray-100 last:border-0 hover:bg-white/60"
                                              >
                                                {impIdx === 0 ? (
                                                  <td
                                                    className="px-3 py-2 font-mono text-gray-800 align-top"
                                                    rowSpan={(impacts ?? []).length}
                                                  >
                                                    {attr}
                                                  </td>
                                                ) : null}
                                                <td className="px-3 py-2">
                                                  <code className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                                                    {imp.ref}
                                                  </code>
                                                </td>
                                                <td className="px-3 py-2">
                                                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                                    UPDATE_MODE_COLORS[imp.mode ?? 'SET'] ?? 'bg-gray-100 text-gray-700'
                                                  }`}>
                                                    {imp.mode ?? 'SET'}
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                  {imp.expr ? (
                                                    <code className="text-xs bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">
                                                      {imp.expr}
                                                    </code>
                                                  ) : (
                                                    <span className="text-gray-300">—</span>
                                                  )}
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <EventRuleModal existing={editTarget} accId={accId} onClose={handleCloseModal} />
      )}
    </div>
  )
}
